import { useState, useCallback } from 'react'
import { useApiFetch, useApiMutation } from '../../hooks/useApiFetch.js'
import { useUIStore } from '../../stores/uiStore.js'
import { Button, Input, Modal, Select, Table, Alert, type TableColumn } from '../../components/ui/index.js'
import styles from './AdminSettings.module.css'

interface AppSetting {
  key: string
  value: unknown
  category: string
  label: string
  description: string | null
  createdAt: string
  updatedAt: string
}

type SettingsResponse = AppSetting[]

export function AdminSettings() {
  const { addToast } = useUIStore()
  const [categoryFilter, setCategoryFilter] = useState('')

  const params = new URLSearchParams()
  if (categoryFilter) params.set('category', categoryFilter)

  const { data, isLoading, error, refetch } = useApiFetch<SettingsResponse>(
    `/api/admin/settings?${params.toString()}`
  )

  const { trigger, isLoading: patchLoading } = useApiMutation('/api/admin/settings', 'PATCH')

  // 모달 상태
  const [modalOpen, setModalOpen] = useState(false)
  const [editKey, setEditKey] = useState('')
  const [editValue, setEditValue] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [editLabel, setEditLabel] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isNew, setIsNew] = useState(false)

  const categories = data ? [...new Set(data.map((s) => s.category))].sort() : []

  const openEdit = useCallback((setting: AppSetting) => {
    setEditKey(setting.key)
    setEditValue(typeof setting.value === 'string' ? setting.value : JSON.stringify(setting.value))
    setEditCategory(setting.category)
    setEditLabel(setting.label)
    setEditDescription(setting.description ?? '')
    setIsNew(false)
    setModalOpen(true)
  }, [])

  const openNew = useCallback(() => {
    setEditKey('')
    setEditValue('')
    setEditCategory('')
    setEditLabel('')
    setEditDescription('')
    setIsNew(true)
    setModalOpen(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!editKey || !editCategory || !editLabel) {
      addToast({ type: 'error', message: 'key, category, label은 필수입니다.' })
      return
    }

    let parsedValue: unknown = editValue
    try {
      parsedValue = JSON.parse(editValue)
    } catch {
      // 문자열 그대로 사용
    }

    const body: Record<string, unknown> = {
      key: editKey,
      value: parsedValue,
      category: editCategory,
      label: editLabel,
    }
    if (editDescription) body.description = editDescription

    const result = await trigger(body)
    if (result) {
      addToast({ type: 'success', message: '설정이 저장되었습니다.' })
      setModalOpen(false)
      refetch()
    } else {
      addToast({ type: 'error', message: '설정 저장에 실패했습니다.' })
    }
  }, [editKey, editValue, editCategory, editLabel, editDescription, trigger, addToast, refetch])

  const columns: TableColumn<AppSetting>[] = [
    { key: 'category', label: '카테고리', width: '100px' },
    { key: 'key', label: 'Key', render: (row) => <span className={styles.keyCell}>{row.key}</span> },
    { key: 'label', label: '라벨' },
    {
      key: 'value',
      label: '값',
      render: (row) => {
        const str = typeof row.value === 'string' ? row.value : JSON.stringify(row.value)
        return <span className={styles.valueCell}>{str.length > 50 ? str.slice(0, 50) + '...' : str}</span>
      },
    },
    {
      key: 'actions',
      label: '',
      width: '60px',
      render: (row) => (
        <button className={styles.editLink} onClick={() => openEdit(row)}>편집</button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Select
          size="sm"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          options={[
            { value: '', label: '전체 카테고리' },
            ...categories.map((c) => ({ value: c, label: c })),
          ]}
        />
        <Button size="sm" onClick={openNew}>새 설정 추가</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AppSetting>
        columns={columns}
        data={data ?? []}
        rowKey={(row) => row.key}
        isLoading={isLoading}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={isNew ? '새 설정 추가' : '설정 편집'} size="sm">
        <div className={styles.modalContent}>
          <Input
            label="Key"
            value={editKey}
            onChange={(e) => setEditKey(e.target.value)}
            disabled={!isNew}
          />
          <Input
            label="카테고리"
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value)}
          />
          <Input
            label="라벨"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
          />
          <Input
            label="값 (JSON 또는 문자열)"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
          <Input
            label="설명 (선택)"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>취소</Button>
            <Button loading={patchLoading} onClick={handleSave}>저장</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
